import React, { useState, useEffect, useCallback, useRef } from 'react';
import KPISummary from './KPISummary';
import ActivityFeed from './ActivityFeed';
import DailyInsights from './DailyInsights';
import AIInsights from './AIInsights';
import PendingReviewSection from './PendingReviewSection';
import DateRangePicker from './DateRangePicker';
import { dashboardService } from '../../services/dashboardService';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

/**
 * DashboardPage component displays the main dashboard with KPIs, activity feed,
 * daily insights, and AI-generated insights for the RAG chatbot system.
 */
const DashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  // State for date range filtering (initialize with default last 3 months)
  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 3);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };
  
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [isInitialized, setIsInitialized] = useState(true); // Start as initialized

  // Dashboard data states
  const [kpiData, setKpiData] = useState({
    activeConversations: 0,
    activeUsers: 0,
    resolvedQueries: 0,
    pendingResponses: 0
  });
  const [activityData, setActivityData] = useState([]);
  const [insightsData, setInsightsData] = useState({
    unreadMessages: { count: 0, change: 0 },
    openQueries: { count: 0, change: 0 },
    actionItems: { count: 0, highPriority: 0 }
  });
  const [aiInsights, setAiInsights] = useState([]);

  /**
   * Fetch all dashboard data from the API
   */
  // Latest-wins guard: React StrictMode (dev) and overlapping refreshes can fire
  // this twice; without it, a stale/aborted run's fallback zeros could overwrite
  // good data. Only the most recent invocation is allowed to update state.
  const fetchIdRef = useRef(0);

  const fetchDashboardData = useCallback(async (customDateRange = null) => {
    const myId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const { startDate, endDate } = customDateRange || dateRange;

      // Fetch all data concurrently for better performance
      const [kpiResponse, activityResponse, insightsResponse, aiInsightsResponse] = await Promise.all([
        dashboardService.getKPISummary(startDate, endDate),
        dashboardService.getActivityFeed(10, startDate, endDate),
        dashboardService.getDailyInsights(),
        dashboardService.getAIInsights()
      ]);

      if (myId !== fetchIdRef.current) return; // a newer fetch superseded this one

      setKpiData(kpiResponse);
      setActivityData(activityResponse);
      setInsightsData(insightsResponse);
      setAiInsights(aiInsightsResponse);
      setLastRefresh(new Date());
    } catch (error) {
      if (myId !== fetchIdRef.current) return;
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please try again later.');
    } finally {
      if (myId === fetchIdRef.current) setLoading(false);
    }
  }, [dateRange]);
  
  /**
   * Manually trigger metrics calculation and refresh dashboard
   */
  const handleRefreshMetrics = async () => {
    if (refreshing) return;
    
    setRefreshing(true);
    setError(null);
    
    try {
      // Trigger metrics calculation
      await dashboardService.calculateMetrics();
      
      // Fetch updated dashboard data
      await fetchDashboardData();
      
      // Show success message (could use a toast notification here)
      console.log('Dashboard metrics refreshed successfully');
    } catch (error) {
      console.error('Error refreshing metrics:', error);
      setError('Failed to refresh metrics. Please try again later.');
    } finally {
      setRefreshing(false);
    }
  };

  /**
   * Handle date range change from DateRangePicker
   */
  const handleDateRangeChange = (startDate, endDate) => {
    console.log('[Dashboard] Date range changed:', { startDate, endDate });
    setDateRange({ startDate, endDate });
    if (!isInitialized) {
      setIsInitialized(true);
    }
  };

  // Single fetch path: fires on mount and whenever the date range changes.
  // (The latest-wins guard in fetchDashboardData makes StrictMode's double-invoke
  // safe.) This replaces the old separate mount effect that caused overlapping
  // requests and a race where fallback zeros could overwrite good data.
  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      fetchDashboardData(dateRange);
    }
  }, [dateRange.startDate, dateRange.endDate, fetchDashboardData]);
  
  // Set up refresh interval
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('[Dashboard] Auto-refresh triggered');
      fetchDashboardData();
    }, 5 * 60 * 1000); // 5 minutes
    return () => clearInterval(interval);
  }, [fetchDashboardData]);

  // Enhanced loading state with better UX
  if (loading && !refreshing) {
    return (
      <div className="bg-gray-900 text-white min-h-screen">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold mb-1">Dashboard</h1>
              <p className="text-gray-400">Loading your dashboard data...</p>
            </div>
          </div>
          
          {/* Skeleton loading UI */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-gray-800 p-6 rounded-lg border border-gray-700">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-700 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-700 rounded w-1/4"></div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-400">Fetching dashboard data...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 text-white min-h-screen">
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold mb-1">Dashboard</h1>
            <p className="text-gray-400">
              Welcome back! Here's an overview of your workspace.
              {lastRefresh && (
                <span className="text-xs text-gray-500 ml-2">
                  Last updated: {lastRefresh.toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            <DateRangePicker 
              onDateRangeChange={handleDateRangeChange}
              defaultRange="last3months"
            />
            <button
            onClick={handleRefreshMetrics}
            disabled={refreshing}
            className={`flex items-center px-4 py-2 rounded-md text-white transition-colors ${refreshing ? 'bg-gray-700' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {refreshing ? (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                Refreshing...
              </>
            ) : (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Refresh Metrics
              </>
            )}
          </button>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-900 border-l-4 border-red-500 text-white p-4 mb-6" role="alert">
            <p>{error}</p>
          </div>
        )}
        
        {/* Overview — operational snapshot. Deep-dive metrics live on /analytics. */}
        <KPISummary data={kpiData} />

        {/* Pending Review — full-width banner */}
        <PendingReviewSection className="mt-6" />

        {/* Bento grid: wide activity feed + focused insight column */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <div className="lg:col-span-2">
            <ActivityFeed data={activityData} />
          </div>
          <div className="lg:col-span-1">
            <DailyInsights data={insightsData} />
          </div>
          <div className="lg:col-span-3">
            <AIInsights insights={aiInsights} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
